import {useMemo} from 'react';

import {panelIsActive, searchRegexMatchesPanel} from '../../util/panelbank';
import {useKeyTypes} from '../../util/runhelpers';
import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {useIsSingleRun} from '../MultiRunWorkspace/SingleRunMode';
import {SectionWithRef} from './types';

export const useSectionPanels = (
  sectionWithRef: SectionWithRef,
  searchRegex: RegExp | null,
  isSectionNameSearchMatch: boolean
) => {
  const isSingleRun = useIsSingleRun(); // todo maybe not use context to match?
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  const keyTypes = useKeyTypes(historyKeyInfo.keys);

  // activePanels are the panels that may get rendered.
  // NOTE: this includes panels that match and don't match the panel search regex.
  // we want to keep them in the same array so we can maintain the panel order
  const activePanels = useMemo(() => {
    return sectionWithRef.panels.filter(panel => {
      const isPanelActive = panelIsActive({
        section: sectionWithRef,
        panel,
        isSingleRun,
        historyKeyInfo,
        keyTypes,
      });

      return isPanelActive;
    });
  }, [historyKeyInfo, isSingleRun, keyTypes, sectionWithRef]);

  const filteredPanels = useMemo(() => {
    if (!searchRegex) {
      return activePanels;
    }

    // If the section name matches the search, return all active panels
    // This is to avoid showing empty sections when searching by section name
    if (isSectionNameSearchMatch) {
      return activePanels;
    }

    return activePanels.filter(panel => {
      return searchRegexMatchesPanel(searchRegex, panel);
    });
  }, [activePanels, isSectionNameSearchMatch, searchRegex]);

  return useMemo(() => {
    return {
      activePanels,
      filteredPanels,
    };
  }, [activePanels, filteredPanels]);
};
